import styled, { css } from 'styled-components';
import { applyStyleModifiers } from 'styled-components-modifiers';

// Import styles
import { flex, transitionMain } from 'components/styles';

const iconNavBase = (theme) => css`
	padding: 0rem 0.6rem;

	svg {
		height: 2.6rem;
		width: 2.6rem;

		path {
			fill: ${theme.header.icon};
		}
	}
`;

const vodColor = {
	darkBlue: '#3562a5',
	lightBlue: '#4387d0'
};

const BUTTON_MODIFIERS = {
	primary: ({ theme }) => css`
		background-color: ${theme.palette.primary.main};
		border-color: ${theme.palette.primary.main};
		color: ${theme.white};

		&:hover {
			background-color: ${theme.palette.primary.dark};
			border-color: ${theme.palette.primary.dark};
		}
	`,
	default: ({ theme }) => css`
		background-image: linear-gradient(to bottom, #fff 0, #edeff2 100%);
		border-color: ${theme.grayLightExtra};
		color: ${theme.grayMedium};
		text-align: left;

		&:hover {
			color: ${theme.palette.primary.main};
			box-shadow: 0 0 4px #e1e2e4;
		}
	`,
	reject: () => css`
		position: relative;
		background:
			radial-gradient(
					57.43% 157.84% at 30.18% 5.11%,
					rgba(255, 255, 255, 0.2) 0%,
					rgba(155, 155, 155, 0.2) 36.68%,
					rgba(113, 112, 112, 0.2) 64.6%,
					rgba(78, 78, 78, 0.2) 100%
				)
				/* warning: gradient uses a rotation that is not supported by CSS and may not behave as expected */,
			linear-gradient(0deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3));

		border: 1px solid #e0e0e04d;

		/*  hack responsible for smooth background gradient transition
			gradients are treated as images and ordinary transiton
			doesn't work on them. Opacity hack is used with extra
			pseudo-element
		*/
		&:before {
			position: absolute;
			content: '';
			inset: 0;
			background:
				radial-gradient(
						57.43% 157.84% at 30.18% 5.11%,
						rgba(255, 255, 255, 0.2) 0%,
						rgba(204, 204, 204, 0.2) 36.68%,
						rgba(179, 178, 178, 0.2) 64.6%,
						rgba(176, 176, 176, 0.2) 100%
					)
					/* warning: gradient uses a rotation that is not supported by CSS and may not behave as expected */,
				linear-gradient(0deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3));

			z-index: -1;
			transition: opacity 0.2s ease-in-out;
			opacity: 0;
		}

		&:hover {
			&:before {
				opacity: 1;
			}
		}
	`,
	dropdown: ({ theme }) => css`
		${flex({ jc: 'space-between' })}

		background: ${theme.dropdown.button.bg};
		border-color: ${theme.line};
		color: ${theme.grayMedium};
		text-align: left;

		&:hover {
			color: ${theme.palette.primary.main};
		}
	`,
	icon: () => css`
		background-color: transparent;
		padding: 0;
	`,
	primaryVod: ({ theme, cancel, isNotAvailable, borderColor, resume }) => css`
		border-radius: 3px;
		color: ${theme.button.primaryVod.color};
		background: transparent;
		border: 1px solid ${borderColor ? `${vodColor.lightBlue}` : 'transparent'};
		width: 12.7rem;
		padding: ${resume ? 1.15 : 1.4}rem 0;
		font-size: 1.1rem;
		position: relative;
		z-index: 10;
		overflow: hidden;
		box-shadow: 0px 1px 0px 0px ${theme.black};
		transition: all 0.2s ease-in-out;

		/* hack responsible for gradient border with rounded edges */
		&:before {
			position: absolute;
			top: 0;
			left: 0;
			height: 100%;
			width: 100%;
			content: '';
			display: block;
			background: linear-gradient(0deg, ${theme.button.primaryVod.beforeBgColor1} 12%, ${theme.button.primaryVod.beforeBgColor2} 75%);
			z-index: -2;
		}

		/*  hack responsible for smooth background gradient transition
			gradients are treated as images and ordinary transiton
			doesn't work on them. Opacity hack is used with extra
			pseudo-element
		*/
		&:after {
			position: absolute;
			top: 0;
			left: 0;
			height: 100%;
			width: 100%;
			content: '';
			display: block;
			background: linear-gradient(0deg, #27477f 0%, #1f6b99 53.8%);
			z-index: -1;
			opacity: 0;
			transition: opacity 0.2s ease-in-out;
		}

		&:hover {
			&:after {
				opacity: 1;
			}
		}

		${isNotAvailable &&
		css`
			color: #9ba7b3;
			cursor: not-allowed;

			&:before {
				background: linear-gradient(0deg, 'transparent' 12%, 'transparent' 75%);
			}
		`}

		${cancel &&
		css`
			border: 1px solid ${resume ? '#48484a' : '#6b747d'};
			background: ${resume ? '#303133' : '#455b72'};
			color: ${resume ? '#797979' : '#9ba7b3'};

			&:before {
				background: linear-gradient(0deg, transparent 12%, transparent 75%);
			}
		`}
	`,

	playerIconHover: ({ theme }) => css`
		&:hover {
			svg {
				path,
				text {
					fill: ${theme.white};
				}
			}
		}
	`,
	playerPlay: () => css`
		position: absolute;
		top: 50%;
		left: 50%;
		z-index: 10;
		transform: translate(-50%, -50%);
	`,
	navIcon: ({ theme }) => css`
		${iconNavBase(theme)}
		&:hover {
			color: ${theme.header.iconActive};

			svg {
				path {
					fill: ${theme.header.iconActive};
				}
			}
		}
	`,
	pipIcon: ({ theme, isAvailable }) => css`
		${iconNavBase(theme)}
		cursor: ${isAvailable ? 'pointer' : 'not-allowed'};

		svg {
			path {
				fill: ${isAvailable ? theme.palette.primary.main : theme.grayMedium};
			}
		}
	`,
	parental: ({ theme }) => css`
		width: 19rem;
		height: 5rem;
		border-radius: 2px;
		font-size: 1.6rem;
		font-weight: ${theme.font.medium};
		background: linear-gradient(to bottom, ${theme.button.parental.bgColor1} 0, ${theme.button.parental.bgColor2} 100%);
	`
};

const Button = styled.button`
	padding: ${({ padding }) => padding || '1rem 1.2rem'};
	font-size: 1.3rem;
	line-height: 1.28;
	border-radius: 5px;
	font-weight: ${({ theme }) => theme.font.medium};
	cursor: ${({ isFocus = true }) => (isFocus ? 'pointer' : 'none')};
	border: 1px solid transparent;
	user-select: none;
	width: ${({ width = 'auto' }) => width};
	background-color: transparent;

	${transitionMain}
	svg {
		path {
			${transitionMain}
		}
	}

	&:focus {
		outline: none;
	}

	&:disabled {
		opacity: 0.2;
		cursor: not-allowed;
	}

	${({ isCatchupControllers }) =>
		isCatchupControllers &&
		css`
			svg {
				height: 3rem;
				width: 3rem;
			}
		`}

	${applyStyleModifiers(BUTTON_MODIFIERS)};
`;

export default Button;
